import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Sparkles, TrendingUp, MapPin, Users, FileText, Eye, UserPlus, Activity, Target, Smartphone, ChevronLeft, ChevronRight, Flame, Heart } from "lucide-react";
import SEO from '../../common/SEO';

const prompts = [
  {
    id: 1,
    title: "Overall Traffic Trends",
    description: "Sessions & Users Over Time",
    category: "Traffic",
    icon: TrendingUp,
    trending: true,
    usageCount: 2847,
    prompt: "Provide an overview of my website's traffic. How many sessions and users did we have each day, and what trends do you see? Please include at least two graphs showing daily sessions and active users."
  },
  {
    id: 2,
    title: "Traffic by Source / Channel",
    description: "Acquisition breakdown",
    category: "Acquisition",
    icon: Sparkles,
    trending: true,
    usageCount: 2156,
    prompt: "Break down my traffic by acquisition source or channel. Which channels brought in the most users and sessions? Include a visual breakdown."
  },
  {
    id: 3,
    title: "Audience Location",
    description: "Geographic distribution",
    category: "Audience",
    icon: MapPin,
    trending: false,
    usageCount: 1432,
    prompt: "Where are my visitors located? Provide a breakdown of users by country or region and highlight the top locations driving traffic."
  },
  {
    id: 4,
    title: "User Demographics",
    description: "Age and gender insights",
    category: "Audience",
    icon: Users,
    trending: false,
    usageCount: 987,
    prompt: "What can you tell me about my users' demographics? Summarize the age and gender distribution of my visitors."
  },
  {
    id: 5,
    title: "Top Landing Pages",
    description: "Session entry points",
    category: "Content",
    icon: FileText,
    trending: true,
    usageCount: 3200,
    prompt: "Which pages are my top landing pages? Identify the pages where sessions most often begin and summarize their performance."
  },
  {
    id: 6,
    title: "Top Pages by Views",
    description: "Most viewed content",
    category: "Content",
    icon: Eye,
    trending: false,
    usageCount: 1245,
    prompt: "What are the most viewed pages on my site? List the top pages or screens by total views and include key engagement insights."
  },
  {
    id: 7,
    title: "New vs Returning Users",
    description: "Visitor loyalty analysis",
    category: "Audience",
    icon: UserPlus,
    trending: false,
    usageCount: 876,
    prompt: "How many visitors are new versus returning? Analyze the mix of new and returning users and explain what this reveals about user behavior."
  },
  {
    id: 8,
    title: "User Engagement Metrics",
    description: "Engagement time & rate",
    category: "Engagement",
    icon: Activity,
    trending: true,
    usageCount: 1654,
    prompt: "How engaged are my users? Report on user engagement metrics such as average engagement time per session and engagement rate."
  },
  {
    id: 9,
    title: "Conversions and Conversion Rate",
    description: "Goal performance",
    category: "Conversions",
    icon: Target,
    trending: true,
    usageCount: 2034,
    prompt: "How are my conversions performing? Summarize the key conversion events, including their counts and conversion rates."
  },
  {
    id: 10,
    title: "Device / Platform Breakdown",
    description: "Mobile, desktop, tablet",
    category: "Technology",
    icon: Smartphone,
    trending: false,
    usageCount: 654,
    prompt: "Which devices are people using to access my site? Show me the breakdown of users or sessions by device category such as mobile, desktop, and tablet."
  }
];

const ITEMS_PER_PAGE = 5;

const categories = ["All", "Trending", "Traffic", "Acquisition", "Audience", "Content", "Engagement", "Conversions", "Technology"];

const PromptLibrary = () => {
  const [copiedId, setCopiedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("Trending");

  const filteredPrompts = selectedCategory === "All" 
    ? prompts 
    : selectedCategory === "Trending"
    ? prompts.filter(p => p.trending).sort((a, b) => b.usageCount - a.usageCount)
    : prompts.filter(p => p.category === selectedCategory);

  const totalPages = Math.ceil(filteredPrompts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentPrompts = filteredPrompts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const categoryColors = {
    Traffic: "bg-blue-50 text-blue-600 border-blue-100",
    Acquisition: "bg-purple-50 text-purple-600 border-purple-100",
    Audience: "bg-green-50 text-green-600 border-green-100",
    Content: "bg-orange-50 text-orange-600 border-orange-100",
    Engagement: "bg-pink-50 text-pink-600 border-pink-100",
    Conversions: "bg-emerald-50 text-emerald-600 border-emerald-100",
    Technology: "bg-slate-50 text-slate-600 border-slate-100",
  };

  const formatUsageCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <SEO
        title="GA4 Prompt Library — Ready-to-Use Analytics Prompts"
        description="Browse trending GA4 prompts for traffic analysis, audience insights, conversion tracking, and more. Copy and use them instantly with the GA4.so AI agent."
        path="/prompt-library"
        rawTitle
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "GA4 Prompt Library",
          "description": "Ready-to-use Google Analytics 4 prompts for traffic, audience, conversion, and engagement analysis.",
          "url": "https://www.ga4.so/prompt-library"
        }}
      />
      {/* Header */}
      <div className="border-b border-slate-200/60 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-slate-900 tracking-tight">
                  Prompt Library
                </h1>
                <p className="text-slate-500 text-xs">
                  {filteredPrompts.length} {selectedCategory === "All" ? "" : selectedCategory.toLowerCase()} prompts
                </p>
              </div>
            </div>
            <span className="hidden sm:block text-[10px] text-slate-400 px-2 py-1 rounded-md bg-slate-100">Click to copy</span>
          </div>
          
          {/* Category Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mb-1">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`
                  flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all inline-flex items-center gap-1
                  ${selectedCategory === category 
                    ? category === "Trending"
                      ? 'bg-pink-500 text-white'
                      : 'bg-slate-900 text-white' 
                    : category === "Trending"
                    ? 'bg-pink-50 text-pink-600 hover:bg-pink-100 border border-pink-100'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }
                `}
              >
                {category === "Trending" && <Flame className="h-3 w-3" />}
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Prompts List */}
        <div className="space-y-3">
          {currentPrompts.map((item, index) => {
            const IconComponent = item.icon;
            const isCopied = copiedId === item.id;
            
            return (
              <div
                key={item.id}
                onClick={() => copyToClipboard(item.prompt, item.id)}
                className={`
                  group relative bg-white rounded-xl border border-slate-200/80 
                  hover:border-slate-300 hover:shadow-md hover:shadow-slate-200/50
                  transition-all duration-200 cursor-pointer overflow-hidden
                  ${isCopied ? 'ring-2 ring-green-500 ring-offset-1' : ''}
                `}
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200/50 flex items-center justify-center text-slate-600 group-hover:from-brand-pink/10 group-hover:to-brand-pink/5 group-hover:border-brand-pink/20 group-hover:text-brand-pink transition-all duration-200">
                      <IconComponent className="h-5 w-5" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-medium text-slate-900 group-hover:text-brand-pink transition-colors">
                          {item.title}
                        </h3>
                        <span className={`flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full border ${categoryColors[item.category]}`}>
                          {item.category}
                        </span>
                        {item.trending && (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-pink-50 text-pink-600 border border-pink-100">
                            <Flame className="h-3 w-3" />
                            Hot
                          </span>
                        )}
                        <span className="flex-shrink-0 inline-flex items-center gap-1 text-slate-400 text-xs ml-auto">
                          <Heart className="h-3.5 w-3.5 fill-current" />
                          {formatUsageCount(item.usageCount)}
                        </span>
                      </div>
                      <div className="flex items-end gap-3">
                        <p className="flex-1 text-sm text-slate-600 leading-relaxed">
                          {item.prompt}
                        </p>
                        {/* Copy Button */}
                        <button
                          className={`
                            flex-shrink-0 p-1.5 rounded-md transition-all duration-200 opacity-0 group-hover:opacity-100
                            ${isCopied 
                              ? 'bg-green-100 text-green-600 opacity-100' 
                              : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                            }
                          `}
                        >
                          {isCopied ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Copied Overlay */}
                {isCopied && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/95 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="h-4 w-4" />
                      <span className="text-sm font-medium">Copied to clipboard</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {currentPrompts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">No prompts found in this category</p>
          </div>
        )}

        {/* Pagination */}
        {filteredPrompts.length > ITEMS_PER_PAGE && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-slate-500">
              {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredPrompts.length)} of {filteredPrompts.length}
            </p>
            
            <div className="flex items-center gap-1">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-slate-200 transition-all"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`
                    min-w-[32px] h-8 px-2.5 rounded-lg text-sm font-medium transition-all
                    ${currentPage === page 
                      ? 'bg-slate-900 text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                    }
                  `}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-slate-200 transition-all"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptLibrary;
